<template>
  <div class="feature-request-card">
    <div class="vote-section">
      <div class="vote-count">
        {{ request.votes }}
      </div>
      <div class="vote-label">
        votes
      </div>
    </div>
    <div class="content-section">
      <div
          class="title-row"
          @click="toggleExpanded">
        <h3 class="title">
          {{ request.title }}
        </h3>
        <i
            class="expand-icon"
            :class="expanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
      </div>
      <transition name="expand">
        <div
            v-show="expanded"
            class="message">
          {{ request.message }}
        </div>
      </transition>
      <div class="actions">
        <el-button
            :type="hasVoted ? 'info' : 'primary'"
            size="small"
            :loading="upvoting"
            :disabled="hasVoted"
            @click="handleUpvote">
          <i :class="hasVoted ? 'el-icon-check' : 'el-icon-caret-top'" />
          {{ hasVoted ? 'Voted' : 'Upvote' }}
        </el-button>
        <el-button
            v-if="canDelete"
            type="danger"
            size="small"
            :loading="deleting"
            @click="handleDelete">
          <i class="el-icon-delete" />
          Delete
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import featureRequestPermissions from "@/mixins/featureRequestPermissions";

export default {
  name: "FeatureRequestCard",
  mixins: [featureRequestPermissions],
  props: {
    request: {
      type: Object,
      required: true,
      validator(value) {
        return (
          value &&
          typeof value.id === "number" &&
          typeof value.title === "string" &&
          typeof value.message === "string" &&
          typeof value.votes === "number" &&
          typeof value.owner === "string"
        );
      },
    },
  },
  data() {
    return {
      upvoting: false,
      deleting: false,
      expanded: false,
    };
  },
  computed: {
    canDelete() {
      return this.canDeleteFeatureRequest(this.request);
    },
    hasVoted() {
      return this.$store.getters["featureRequests/hasVoted"](this.request.id);
    },
  },
  methods: {
    toggleExpanded() {
      this.expanded = !this.expanded;
    },
    handleUpvote() {
      // Check if user has already voted
      if (this.hasVoted) {
        this.$message({
          type: "warning",
          message: "You have already voted on this request.",
          duration: 2000,
        });
        return;
      }

      this.upvoting = true;
      this.$store
        .dispatch("featureRequests/upvoteFeatureRequest", this.request.id)
        .then(() => {
          this.$message({
            type: "success",
            message: "Successfully upvoted!",
            duration: 2000,
          });
          this.upvoting = false;
        })
        .catch((error) => {
          const message =
            error.response?.data?.message ||
            "Failed to upvote. You may have already voted on this request.";
          this.$message({
            type: "error",
            message,
            duration: 3000,
          });
          this.upvoting = false;
        });
    },
    handleDelete() {
      this.$confirm(
        "Are you sure you want to delete this feature request? This action cannot be undone.",
        "Confirm Deletion",
        {
          confirmButtonText: "Delete",
          cancelButtonText: "Cancel",
          type: "warning",
          confirmButtonClass: "el-button--danger",
        }
      )
        .then(() => {
          this.deleting = true;
          return this.$store.dispatch(
            "featureRequests/deleteFeatureRequest",
            this.request.id
          );
        })
        .then(() => {
          this.$message({
            type: "success",
            message: "Feature request deleted successfully",
            duration: 2000,
          });
          this.deleting = false;
        })
        .catch((error) => {
          // User cancelled or error occurred
          if (error !== "cancel") {
            const message =
              error.response?.data?.message ||
              "Failed to delete feature request";
            this.$message({
              type: "error",
              message,
              duration: 3000,
            });
          }
          this.deleting = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.feature-request-card {
  display: flex;
  border: 1px solid #e4e4e4;
  border-radius: 4px;
  padding: 20px;
  margin-bottom: 15px;
  background: #fff;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  }

  .vote-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    min-width: 80px;
    padding-right: 20px;
    border-right: 1px solid #e4e4e4;

    .vote-count {
      font-size: 36px;
      font-weight: bold;
      color: $--color-curious-blue;
      line-height: 1;
    }

    .vote-label {
      font-size: 14px;
      color: $--color-dusty-gray;
      margin-top: 5px;
    }
  }

  .content-section {
    flex: 1;
    padding-left: 20px;
    display: flex;
    flex-direction: column;

    .title-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      cursor: pointer;
      margin-bottom: 10px;
      user-select: none;

      &:hover {
        .title {
          color: $--color-curious-blue;
        }
      }

      .title {
        font-size: 18px;
        font-weight: 500;
        color: $--color-tundora;
        margin: 0;
        flex: 1;
        transition: color 0.2s ease;
      }

      .expand-icon {
        font-size: 18px;
        color: $--color-dusty-gray;
        margin-left: 10px;
        transition: transform 0.3s ease;
      }
    }

    .message {
      font-size: 15px;
      color: $--color-dusty-gray;
      line-height: 1.6;
      margin-bottom: 15px;
      padding: 10px 0;
      border-top: 1px solid #f0f0f0;
    }

    .expand-enter-active,
    .expand-leave-active {
      transition: all 0.3s ease;
      max-height: 500px;
      overflow: hidden;
    }

    .expand-enter,
    .expand-leave-to {
      max-height: 0;
      opacity: 0;
    }

    .actions {
      display: flex;
      gap: 10px;
      margin-top: auto;

      .el-button {
        i {
          margin-right: 5px;
        }
      }
    }
  }
}
</style>

